/**
 * LadiesHubSubcategorySheet — Premium Bottom Sheet with helper text
 * Single-column list with icon, title, and helper text
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { getSubcategoryIcon } from '../config/ladiesSubcategoryIcons';
import { hapticSelection, hapticImpact } from '../utils/haptics';
import './LadiesHubSubcategorySheet.css';

export default function LadiesHubSubcategorySheet({
  isOpen,
  onClose,
  parentTab,
  subcategories,
  onSelect,
  lang = 'en',
}) {
  const [selected, setSelected] = useState(null);
  const isArabic = lang === 'ar';
  const Chevron = isArabic ? ChevronLeft : ChevronRight;

  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen, parentTab?.id]);

  const getLabel = (item) =>
    isArabic ? item.ar || item.label_ar : item.en || item.label_en;

  const getHelper = (item) =>
    isArabic ? item.helper_ar || '' : item.helper_en || '';

  const parentLabel = parentTab
    ? isArabic
      ? parentTab.label_ar
      : parentTab.label_en
    : '';

  const handleCardClick = async (subcat) => {
    await hapticSelection();
    setSelected(subcat.id === selected ? null : subcat.id);
  };

  const handleConfirm = async () => {
    if (!selected) return;
    const subcat = subcategories.find((s) => s.id === selected);
    if (subcat) {
      await hapticImpact('light');
      onSelect(subcat);
      onClose();
    }
  };

  const handleViewAll = async () => {
    if (parentTab) {
      await hapticImpact('light');
      onSelect({ id: parentTab.id, isParent: true });
      onClose();
    }
  };

  const handleClose = async () => {
    await hapticSelection();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="scs-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            data-testid="subcategory-sheet-backdrop"
          />

          <motion.div
            className="scs-sheet"
            dir={isArabic ? 'rtl' : 'ltr'}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 340 }}
            data-testid="subcategory-sheet"
          >
            <div className="scs-handle" />

            <div className="scs-header">
              <div className="scs-header-text">
                <h3 className="scs-title">{parentLabel}</h3>
                <p className="scs-helper">
                  {isArabic
                    ? 'اختاري تصنيفاً فرعياً للمتابعة'
                    : 'Choose a subcategory to continue'}
                </p>
              </div>
              <button
                className="scs-close"
                onClick={handleClose}
                data-testid="subcategory-sheet-close"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            <div className="scs-body">
              <div className="scs-list">
                {subcategories.map((subcat, index) => {
                  const isSelected = subcat.id === selected;
                  const IconComponent = getSubcategoryIcon(subcat.id);
                  const helperText = getHelper(subcat);
                  return (
                    <motion.button
                      key={subcat.id}
                      className={`scs-card${isSelected ? ' scs-card-selected' : ''}`}
                      onClick={() => handleCardClick(subcat)}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.025, duration: 0.2 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid={`subcat-${subcat.id}`}
                    >
                      <span className="scs-card-icon">
                        <IconComponent size={18} strokeWidth={1.8} />
                      </span>
                      <span className="scs-card-text">
                        <span className="scs-card-label">{getLabel(subcat)}</span>
                        {helperText && (
                          <span className="scs-card-helper">{helperText}</span>
                        )}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div className="scs-footer">
              <button
                className={`scs-cta${selected ? ' scs-cta-active' : ''}`}
                onClick={handleConfirm}
                disabled={!selected}
                data-testid="subcategory-confirm-btn"
              >
                <span>
                  {selected
                    ? isArabic
                      ? `عرض ${getLabel(subcategories.find((s) => s.id === selected) || {})}`
                      : `Browse ${getLabel(subcategories.find((s) => s.id === selected) || {})}`
                    : isArabic
                    ? 'اختاري تصنيفاً'
                    : 'Select a subcategory'}
                </span>
                {selected && <Chevron size={16} className="scs-cta-chevron" />}
              </button>

              <button
                className="scs-view-all"
                onClick={handleViewAll}
                data-testid="view-all-category"
              >
                {isArabic
                  ? `عرض كل ${parentLabel}`
                  : `View all in ${parentLabel}`}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
